/**
 * Editorial AI Review Service (NWFE-RQ-01.4)
 * Admin-only: AI shadow-review monitoring + prompt management.
 */

import axios from '../utils/axios';

const API_URL = '/editorial-workflow/ai';

// ── Types ───────────────────────────────────────────────────────────────────

export type AiSubagent = 'linguistic' | 'pedagogical';
export type AiReviewStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface AiReviewContent {
  stem: string;
  explanation: string;
  correctAnswer?: string;
  alternatives?: Array<{ letter: string; text: string }>;
}

export interface AiReviewFilters {
  stage?: number;
  status?: string;
  search?: string;
  portfolio?: string;
  from?: string;
  to?: string;
  // Base-scope parity with the Question Bank (client request — Lucas)
  oeCode?: string;
  type?: string;
  difficulty?: string;
  taxonomyNode?: string;
}

export interface AiReviewIssue {
  ruleRef?: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  confidence: number;
  suggestion?: string;
  source: 'deterministic' | 'llm';
}

export interface AiReviewRun {
  _id: string;
  taskId: string;
  questionId: string;
  portfolios?: string[]; // stamped at run creation (client/request identity)
  question?: {
    _id: string;
    oeCode: string;
    type: string;
    difficulty?: string;
    correctAnswer?: string;
    taxonomyPaths?: string[];
    stemPreview: string;
  };
  stage: 1 | 2;
  subagent: AiSubagent;
  status: AiReviewStatus;
  inputContent?: AiReviewContent;
  suggestedContent?: AiReviewContent;
  issues: AiReviewIssue[];
  summary?: string;
  promptSource?: { source: string; configId?: string; version?: number };
  llmModel?: string;
  tokensUsed?: number;
  processingTimeMs?: number;
  error?: string;
  humanFinalContent?: AiReviewContent;
  divergence?: {
    fields: Record<string, { aiChanged: boolean; humanChanged: boolean; aiEqualsHuman: boolean }>;
    agreementRate: number;
    computedAt: string;
  };
  createdAt: string;
  completedAt?: string;
}

export interface AiReviewListResult {
  reviews: AiReviewRun[];
  summary: Record<string, { count: number; avgAgreement: number | null; avgTimeMs: number | null }>;
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface PromptConfig {
  _id: string;
  subagent: AiSubagent;
  level: 'stage' | 'question_type' | 'oe';
  questionType?: string | null;
  oeCode?: string | null;
  activeVersion: number;
  isActive: boolean;
  activeContent: string;
  activeVersionNotes?: string;
  activeVersionAuthor?: string;
  activeVersionAt?: string;
  versionsCount: number;
}

export interface PromptVersion {
  _id: string;
  configId: string;
  version: number;
  content: string;
  notes?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

export interface PromptListResult {
  configs: PromptConfig[];
  defaults: Record<AiSubagent, string>;
}

export interface UpsertPromptInput {
  subagent: AiSubagent;
  level: 'stage' | 'question_type' | 'oe';
  questionType?: string;
  oeCode?: string;
  content: string;
  notes?: string;
}

// ── Service ─────────────────────────────────────────────────────────────────

class EditorialAiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }

  async listReviews(
    filters: AiReviewFilters & { page?: number; limit?: number } = {}
  ): Promise<AiReviewListResult> {
    const response = await axios.get(`${API_URL}/reviews`, {
      headers: this.getAuthHeaders(),
      params: filters,
    });
    return response.data.data;
  }

  async getTaskReviews(taskId: string): Promise<AiReviewRun[]> {
    const response = await axios.get(`${API_URL}/tasks/${taskId}/reviews`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  async requeueMissing(): Promise<{ queuedStage1: number; queuedStage2: number }> {
    const response = await axios.post(
      `${API_URL}/reviews/requeue-missing`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }

  async requeueStale(): Promise<{ scanned: number; requeued: number }> {
    const response = await axios.post(
      `${API_URL}/reviews/requeue-stale`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }

  async rerun(taskId: string, stage: 1 | 2): Promise<void> {
    await axios.post(
      `${API_URL}/tasks/${taskId}/reviews/${stage}/rerun`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  async exportReviews(
    filters: AiReviewFilters,
    format: 'jsonl' | 'csv' | 'xlsx'
  ): Promise<Blob> {
    const response = await axios.get(`${API_URL}/reviews/export`, {
      headers: this.getAuthHeaders(),
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  }

  // ── Prompts ──

  async listPrompts(): Promise<PromptListResult> {
    const response = await axios.get(`${API_URL}/prompts`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  async upsertPrompt(input: UpsertPromptInput): Promise<PromptConfig> {
    const response = await axios.post(`${API_URL}/prompts`, input, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  async getPromptVersions(configId: string): Promise<PromptVersion[]> {
    const response = await axios.get(`${API_URL}/prompts/${configId}/versions`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  async rollbackPrompt(configId: string, version: number): Promise<PromptConfig> {
    const response = await axios.post(
      `${API_URL}/prompts/${configId}/rollback`,
      { version },
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }

  async deactivatePrompt(configId: string): Promise<void> {
    await axios.delete(`${API_URL}/prompts/${configId}`, {
      headers: this.getAuthHeaders(),
    });
  }
}

export const editorialAiService = new EditorialAiService();
